import React from "react";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { SLIDING_EMPTY_SLOT_OPACITY, slidingEmptySlotBackground } from "./slidingEmptySlotStyle";

export { SLIDING_EMPTY_SLOT_OPACITY, slidingEmptySlotBackground };

/**
 * Full story image with gs×gs grid lines. Optionally tints the bottom-right cell
 * (canonical empty slot for slice index n−1 in the solved picture).
 */
export default function StoryReferenceWithGrid({
    imageUrl,
    gridSize,
    alt = "",
    /** When true, image fills a square frame (`object-fit: cover`) so it aligns with a square sliding board. */
    squareFrame = false,
    /** When true, parent is already square-sized; use 100%×100% (keeps 1:1, no stretch). */
    fillSquareParent = false,
    /** Fill the parent box (any aspect); image covers, no 1:1 lock. */
    fillParent = false,
    /** When false, omit the bottom-right empty-slot tint (browse cards show the full original). */
    showEmptySlot = true,
    /** CSS color for the hole. Defaults to the shared gray tint. */
    emptySlotBackground = null,
    /** Remove the photo in the hole so only the transparent fill remains. */
    punchEmptySlot = false,
    /** Opacity for the bottom-right “empty slot” tint (canonical hole). */
    emptySlotOpacity = SLIDING_EMPTY_SLOT_OPACITY,
    /** Grid gutter color (default near-white for photo boards). */
    lineColor = "rgba(255, 255, 255, 0.92)",
    /** When false, show the plain picture (browse cards keep the artwork clean). */
    showGridLines = true,
}) {
    const gs = Number(gridSize);

    /** Must match `SlidingPlayMain` grid `gap` so reference lines align with puzzle gutters. */
    const GRID_GAP_PX = 2;

    /** Bottom-right cell (row gs−1, col gs−1) — solved-image position of the empty tile. */
    const row = gs > 0 ? gs - 1 : -1;
    const col = gs > 0 ? gs - 1 : -1;

    if (!imageUrl || !Number.isFinite(gs) || gs < 1) {
        return null;
    }

    const line = lineColor || "rgba(255, 255, 255, 0.92)";
    /** Track length (one tile) — must match CSS Grid `1fr` + `gap` in SlidingPlayMain. */
    const cellTrack = `calc((100% - ${(gs - 1) * GRID_GAP_PX}px) / ${gs})`;
    const holeGap = (gs - 1) * GRID_GAP_PX;
    const holeLeft = `calc(${col} * (100% - ${holeGap}px) / ${gs} + ${col * GRID_GAP_PX}px)`;
    const holeTop = `calc(${row} * (100% - ${holeGap}px) / ${gs} + ${row * GRID_GAP_PX}px)`;
    const holeRight = `calc(${col} * (100% - ${holeGap}px) / ${gs} + ${col * GRID_GAP_PX}px + (100% - ${holeGap}px) / ${gs})`;
    const holeBottom = `calc(${row} * (100% - ${holeGap}px) / ${gs} + ${row * GRID_GAP_PX}px + (100% - ${holeGap}px) / ${gs})`;
    const punchHole = Boolean(punchEmptySlot && showEmptySlot && row >= 0 && col >= 0);

    /**
     * 2px gutters drawn at tile boundaries (same rhythm as puzzle grid gap).
     * Do not set `backgroundSize` here — tiling the layer again repeats the whole stripe pattern
     * into a stamp and causes a dense “mesh” of lines.
     */
    const gridOverlaySx = {
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 2,
        backgroundImage: `
            repeating-linear-gradient(to right,
                transparent 0,
                transparent ${cellTrack},
                ${line} ${cellTrack},
                ${line} calc(${cellTrack} + ${GRID_GAP_PX}px)
            ),
            repeating-linear-gradient(to bottom,
                transparent 0,
                transparent ${cellTrack},
                ${line} ${cellTrack},
                ${line} calc(${cellTrack} + ${GRID_GAP_PX}px)
            )
        `,
    };

    return (
        <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    lineHeight: 0,
                    overflow: "hidden",
                    ...(fillParent
                        ? {
                              height: "100%",
                              minHeight: 0,
                              minWidth: 0,
                              flex: 1,
                              borderRadius: 0,
                              border: "none",
                              containerType: "size",
                          }
                        : {
                              aspectRatio: "1 / 1",
                              borderRadius: 1,
                              border: 1,
                              borderColor: "divider",
                              ...(fillSquareParent
                                  ? { width: "100%", height: "100%", minHeight: 0, minWidth: 0 }
                                  : squareFrame
                                    ? { width: "100%" }
                                    : {}),
                          }),
                }}
        >
            <Box
                component="img"
                src={imageUrl}
                alt={alt}
                referrerPolicy="no-referrer"
                sx={{
                    display: "block",
                    width: "100%",
                    ...(squareFrame || fillParent
                        ? {
                              height: "100%",
                              objectFit: "cover",
                          }
                        : {
                              height: "auto",
                          }),
                    ...(punchHole
                        ? {
                              clipPath: `polygon(evenodd, 0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${holeLeft} ${holeTop}, ${holeRight} ${holeTop}, ${holeRight} ${holeBottom}, ${holeLeft} ${holeBottom}, ${holeLeft} ${holeTop})`,
                          }
                        : {}),
                    verticalAlign: "top",
                }}
            />

            {showEmptySlot ? (
                <Box
                    sx={(theme) => ({
                        position: "absolute",
                        left: `calc(${col} * (100% - ${(gs - 1) * GRID_GAP_PX}px) / ${gs} + ${col * GRID_GAP_PX}px)`,
                        top: `calc(${row} * (100% - ${(gs - 1) * GRID_GAP_PX}px) / ${gs} + ${row * GRID_GAP_PX}px)`,
                        width: cellTrack,
                        height: cellTrack,
                        backgroundColor: emptySlotBackground || slidingEmptySlotBackground(theme, emptySlotOpacity),
                        pointerEvents: "none",
                        boxSizing: "border-box",
                        zIndex: 1,
                    })}
                />
            ) : null}

            {showGridLines ? (
                fillParent ? (
                    <Box
                        sx={{
                            ...gridOverlaySx,
                            inset: "unset",
                            top: "50%",
                            left: "50%",
                            width: "min(100cqw, 100cqh)",
                            height: "min(100cqw, 100cqh)",
                            transform: "translate(-50%, -50%)",
                            boxShadow: `inset 0 0 0 ${GRID_GAP_PX}px ${line}`,
                        }}
                    />
                ) : (
                    <Box sx={gridOverlaySx} />
                )
            ) : null}
        </Box>
    );
}

StoryReferenceWithGrid.propTypes = {
    imageUrl: PropTypes.string.isRequired,
    gridSize: PropTypes.number.isRequired,
    alt: PropTypes.string,
    squareFrame: PropTypes.bool,
    fillSquareParent: PropTypes.bool,
    fillParent: PropTypes.bool,
    showEmptySlot: PropTypes.bool,
    emptySlotBackground: PropTypes.string,
    punchEmptySlot: PropTypes.bool,
    emptySlotOpacity: PropTypes.number,
    lineColor: PropTypes.string,
    showGridLines: PropTypes.bool,
};
